import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Clock, Mail, Send } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-md border border-ink/20 bg-cream px-4 py-3 text-sm text-ink placeholder:text-slate2/60 outline-none transition-colors duration-200 focus:border-plum focus:ring-2 focus:ring-plum/15";

export const Visit = () => {
  const [form, setForm] = useState({ name: "", email: "", preferred_date: "", children_ages: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/visits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Visit enquiry received — we'll write back within a day.");
      setForm({ name: "", email: "", preferred_date: "", children_ages: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="visit"
      data-testid="visit-section"
      className="border-t border-ink/10 bg-sand py-28 md:py-40"
    >
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 px-6 md:px-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.25em] text-plum">
              07 · Plan a Visit
            </p>
            <h2 className="font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl md:text-6xl">
              Come sit with us for an <em className="text-plum">afternoon.</em>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-slate2 md:text-lg">
              The first visit is always free. Bring the children, browse the
              shelves, try the clay table — and see if it feels like yours.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 space-y-6">
              <div data-testid="visit-info-address" className="flex items-start gap-4">
                <MapPin size={20} className="mt-1 shrink-0 text-plum" />
                <div>
                  <p className="font-semibold text-ink">Purple Space Library</p>
                  <p className="text-sm text-slate2">Perampalli, Udupi, Karnataka</p>
                </div>
              </div>
              <div data-testid="visit-info-hours" className="flex items-start gap-4">
                <Clock size={20} className="mt-1 shrink-0 text-plum" />
                <div>
                  <p className="font-semibold text-ink">Open Tuesday – Sunday</p>
                  <p className="text-sm text-slate2">10:00 AM – 7:00 PM · Story circle at 5:30 PM</p>
                </div>
              </div>
              <div data-testid="visit-info-email" className="flex items-start gap-4">
                <Mail size={20} className="mt-1 shrink-0 text-plum" />
                <div>
                  <p className="font-semibold text-ink">hello@purplespacelibrary.in</p>
                  <p className="text-sm text-slate2">We reply within a day, usually sooner</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            data-testid="visit-form"
            onSubmit={submit}
            className="rounded-3xl border border-ink/10 bg-cream p-8 md:p-12"
          >
            <h3 className="font-serif text-3xl text-ink">Tell us you're coming</h3>
            <p className="mt-2 text-sm text-slate2">
              A quick note so we can keep a corner ready for you.
            </p>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="visit-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">
                  Your name
                </label>
                <input id="visit-name" data-testid="visit-form-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Ananya Rao" className={inputCls} />
              </div>
              <div>
                <label htmlFor="visit-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">
                  Email
                </label>
                <input id="visit-email" data-testid="visit-form-email" required type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
              </div>
              <div>
                <label htmlFor="visit-date" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">
                  Preferred date
                </label>
                <input id="visit-date" data-testid="visit-form-date" required type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="visit-ages" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">
                  Children's ages <span className="normal-case text-slate2/70">(optional)</span>
                </label>
                <input id="visit-ages" data-testid="visit-form-ages" value={form.children_ages} onChange={set("children_ages")} placeholder="e.g. 4 and 7" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="visit-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-ink/70">
                  Anything we should know? <span className="normal-case text-slate2/70">(optional)</span>
                </label>
                <textarea id="visit-message" data-testid="visit-form-message" rows={4} value={form.message} onChange={set("message")} placeholder="Interests, allergies, a favourite book…" className={inputCls} />
              </div>
            </div>

            <button
              type="submit"
              data-testid="visit-form-submit"
              disabled={sending}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-plum px-8 py-4 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send Visit Enquiry"}
              <Send size={15} />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
